import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Wallet, Plus, Loader2, Trash2, TrendingDown, TrendingUp, Search, X,
  Download, FileText, Filter, Copy, Edit2, MoreVertical, Archive,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Iter50 → Iter155 — Office Daily Expenses (petty-cash book).
 *
 * Coordinator-first: log petty-cash spend and top-ups. Running balance is
 * derived from the (date ASC, created_at ASC) sorted list so storage stays
 * append-only.
 *
 * iter155 upgrades:
 *   • Default screen shows only the LATEST 10 entries (newest first).
 *   • "Latest 10 | All Entries" toggle — hidden entirely for users who
 *     don't have permission to view the archive (viewer / manager).
 *   • Archive drawer with search + date range + type/category filter +
 *     CSV + PDF export.
 *   • Row actions: Edit · Duplicate · Delete (admin/coordinator only).
 *   • Category tag for filtering & drill-downs.
 *
 * NOTE: Nothing is ever deleted automatically. "Archive" is a UI-only
 * separation — the full history remains queryable through the drawer.
 */
const CATEGORY_SUGGESTIONS = ["PETROL", "CHAI", "FOOD", "OFFICE", "TRAVEL", "ADVANCE", "OWNER TOP-UP", "MISC"];

export default function OfficeExpenses() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0, net_balance: 0 });
  // iter188 — The page now shows the FULL chronological ledger by
  // default; the Latest 10 / All Entries toggle was removed. The
  // rolling 30-day block (iter186 API `totals_30d`) is no longer
  // rendered here — it stays in the API response for other clients.
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited inline
  // iter206 — gates follow the effective `cash_book` permission (same as the API).
  const cashLevel = user?.is_owner || user?.role === "admin"
    ? 3 : ({ view_only: 1, edit: 2, full_control: 3 }[user?.permissions_effective?.cash_book] || 0);
  const canWrite = cashLevel >= 2;
  // iter155 — Archive visibility. Admin + coordinator can browse the
  // full ledger. Viewers / managers see the latest 10 only and the
  // toggle is hidden entirely (per spec).
  // NOTE: This is the P0 permission gate. P2 RBAC will replace this
  // with a centralised permission engine.
  const canViewAll = cashLevel >= 1;
  const canDelete = user?.is_owner || user?.role === "admin"; // iter155 — delete UI stays admin-only

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // iter188 — Always fetch the full ledger; page no longer has
      // a Latest 10 / All Entries toggle.
      const { data } = await api.get("/team/office-expenses");
      setRows(data.rows || []);
      setTotals(data.totals || { debit: 0, credit: 0, net_balance: 0 });
      setTotalCount(data.total_count || (data.rows || []).length);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load expenses");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Rows with computed running balance. When "Latest 10" is active the
  // backend returns the last N rows chronologically (so balance stays
  // correct against the beginning of that slice). For "All" view the
  // running balance walks from zero. This mirrors the classic Excel
  // pattern the studio is used to.
  const enriched = useMemo(() => {
    let bal = 0;
    return rows.map((r) => {
      const debit = r.transaction_type === "debit" ? Number(r.amount) || 0 : 0;
      const credit = r.transaction_type === "credit" ? Number(r.amount) || 0 : 0;
      bal = Math.round((bal + credit - debit) * 100) / 100;
      return { ...r, debit, credit, running_balance: bal };
    });
  }, [rows]);

  // Display order is newest-first per spec — but running balance is
  // computed chronologically first, then the array is reversed.
  const displayRows = useMemo(() => [...enriched].reverse(), [enriched]);

  // iter188 — CURRENT BALANCE is the final chronological running
  // balance of the FULL ledger. `enriched` is the full ledger in
  // chronological order (before reversal), so the last row's
  // running_balance is authoritative — no re-sum needed.
  const currentBalance = enriched.length ? enriched[enriched.length - 1].running_balance : 0;

  const create = async (payload) => {
    try {
      await api.post("/team/office-expenses", payload);
      await load();
      toast.success(`${payload.transaction_type === "debit" ? "Debit" : "Credit"} recorded · ${payload.reason}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save entry");
    }
  };

  const remove = async (id) => {
    if (!canDelete) return;
    const row = rows.find((r) => r.id === id);
    const label = row ? `${row.reason} (${row.date})` : "this entry";
    const ok = typeof window !== "undefined"
      ? window.confirm(`Delete ${label}? This cannot be undone.`)
      : true;
    if (!ok) return;
    try {
      await api.delete(`/team/office-expenses/${id}`);
      await load();
      toast.success("Entry deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const duplicate = async (id) => {
    if (!canWrite) return;
    try {
      await api.post(`/team/office-expenses/${id}/duplicate`);
      await load();
      toast.success("Entry duplicated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not duplicate");
    }
  };

  const saveEdit = async (id, patch) => {
    try {
      await api.patch(`/team/office-expenses/${id}`, patch);
      setEditing(null);
      await load();
      toast.success("Entry updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div className="space-y-6" data-testid="office-expenses-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Cash Book</div>
          <h1 className="font-heading text-2xl font-semibold text-stone-900 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-rose-700" strokeWidth={1.8} />
            Office Daily Expenses
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Log petty-cash spend and top-ups. Running balance updates live.
          </p>
        </div>
        {canViewAll && (
          <button
            type="button"
            onClick={() => setArchiveOpen(true)}
            className="btn btn-outline btn-sm inline-flex items-center gap-1.5"
            data-testid="office-expenses-open-archive"
          >
            <Archive className="w-4 h-4" strokeWidth={1.8} /> View Archive
          </button>
        )}
      </header>

      {/* iter188 — Prominent full-ledger summary. Everyone opening this
           page should immediately see: what's the current balance,
           total spent, total received, and net. All numbers use the
           FULL chronological ledger (never a paginated slice). */}
      <FullLedgerSummary
        currentBalance={currentBalance}
        totalExpenses={totals.debit}
        totalReceived={totals.credit}
        totalCount={totalCount}
        loading={loading}
      />

      {canWrite && <EntryForm onSubmit={create} loading={loading} />}

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-rose-50 to-white border-b border-stone-200 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-stone-800">Ledger</div>
            <span className="text-[10px] uppercase tracking-widest text-stone-400" data-testid="office-expenses-row-count">
              {totalCount} {totalCount === 1 ? "entry" : "entries"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin text-stone-400" />}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="office-expenses-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold text-left">
                <th className="px-3 py-2 w-28">Date</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2 w-28">Category</th>
                <th className="px-3 py-2 text-right w-28">Expense</th>
                <th className="px-3 py-2 text-right w-28">Received</th>
                <th className="px-3 py-2 text-right w-32">Balance</th>
                {canWrite && <th className="px-3 py-2 w-20 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {displayRows.map((r) => (
                editing?.id === r.id ? (
                  <EditRow
                    key={r.id}
                    row={r}
                    onSave={(patch) => saveEdit(r.id, patch)}
                    onCancel={() => setEditing(null)}
                    canWrite={canWrite}
                  />
                ) : (
                  <tr key={r.id} data-testid={`office-expenses-row-${r.id}`}>
                    <td className="px-3 py-2 text-stone-600 font-mono text-xs whitespace-nowrap">
                      {formatDMY(r.date)}
                    </td>
                    <td className="px-3 py-2 font-medium text-stone-800">{r.reason}</td>
                    <td className="px-3 py-2 text-stone-500 text-xs">
                      {r.category ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 text-[10px] uppercase tracking-wider font-semibold">
                          {r.category}
                        </span>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-mono text-red-700" data-testid={`office-expenses-debit-${r.id}`}>
                      {r.debit > 0 ? `-₹${Math.round(r.debit).toLocaleString("en-IN")}` : ""}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-mono text-emerald-700" data-testid={`office-expenses-credit-${r.id}`}>
                      {r.credit > 0 ? `+₹${Math.round(r.credit).toLocaleString("en-IN")}` : ""}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-mono font-semibold ${r.running_balance < 0 ? "text-red-800" : "text-stone-800"}`} data-testid={`office-expenses-balance-${r.id}`}>
                      {inrBalance(r.running_balance)}
                    </td>
                    {canWrite && (
                      <td className="px-2 py-2">
                        <RowActions
                          onEdit={() => setEditing(r)}
                          onDuplicate={() => duplicate(r.id)}
                          onDelete={canDelete ? () => remove(r.id) : null}
                          testid={r.id}
                        />
                      </td>
                    )}
                  </tr>
                )
              ))}
              {displayRows.length === 0 && !loading && (
                <tr>
                  <td colSpan={canWrite ? 7 : 6} className="px-3 py-10 text-center text-stone-400 text-xs">
                    No entries yet. {canWrite ? "Use the form above to record your first petty-cash flow." : "Ask an admin or coordinator to add entries."}
                  </td>
                </tr>
              )}
            </tbody>
            {enriched.length > 0 && (
              // iter188 — Simple full-ledger totals row. Debit/Credit/Net
              // over EVERY entry (never a paginated slice). The prominent
              // summary cards above the table also read from `totals`,
              // so the two blocks are guaranteed to agree.
              <tfoot className="bg-stone-50 border-t-2 border-stone-200">
                <tr className="font-semibold" data-testid="office-expenses-totals-row">
                  <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-stone-600" colSpan={3}>
                    Ledger totals
                    <span className="text-[10px] normal-case text-stone-400 tracking-normal ml-2">
                      {totalCount} {totalCount === 1 ? "entry" : "entries"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono text-red-800" data-testid="office-expenses-totals-debit">
                    {totals.debit > 0 ? `-₹${Math.round(totals.debit).toLocaleString("en-IN")}` : "₹0"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono text-emerald-800" data-testid="office-expenses-totals-credit">
                    {totals.credit > 0 ? `+₹${Math.round(totals.credit).toLocaleString("en-IN")}` : "₹0"}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums font-heading text-base ${currentBalance < 0 ? "text-red-800" : "text-stone-900"}`} data-testid="office-expenses-totals-net">
                    {inrBalance(currentBalance)}
                  </td>
                  {canWrite && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {archiveOpen && canViewAll && (
        <ArchiveDrawer
          onClose={() => setArchiveOpen(false)}
          onChanged={load}
          canWrite={canWrite}
          canDelete={canDelete}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// iter188 — Prominent full-ledger summary block. Everything in this
// component is human-friendly and reads from the FULL ledger — never
// from the visible/paginated slice.
//
//   CURRENT BALANCE  → final running balance across every entry
//   TOTAL EXPENSES   → Σ debit
//   TOTAL RECEIVED   → Σ credit
//   NET              → TOTAL RECEIVED − TOTAL EXPENSES
// -------------------------------------------------------------------
function FullLedgerSummary({ currentBalance, totalExpenses, totalReceived, totalCount, loading }) {
  const net = Math.round(((Number(totalReceived) || 0) - (Number(totalExpenses) || 0)) * 100) / 100;
  const balanceNegative = currentBalance < 0;
  const netNegative = net < 0;
  return (
    <section
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      aria-label="Ledger summary"
      data-testid="office-expenses-summary"
    >
      {/* CURRENT BALANCE — biggest, most prominent card. */}
      <div
        className={`col-span-1 sm:col-span-2 lg:col-span-1 rounded-2xl p-5 border shadow-sm ${
          balanceNegative
            ? "bg-gradient-to-br from-red-50 to-white border-red-200"
            : "bg-gradient-to-br from-emerald-50 to-white border-emerald-200"
        }`}
        data-testid="summary-current-balance-card"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">
          Current Balance
        </div>
        <div
          className={`mt-2 font-heading font-semibold tabular-nums text-4xl lg:text-3xl xl:text-4xl ${
            balanceNegative ? "text-red-700" : "text-emerald-700"
          }`}
          data-testid="summary-current-balance"
        >
          {loading ? "—" : inrBalance(currentBalance)}
        </div>
        <div className="mt-1 text-[11px] text-stone-500">
          {totalCount} {totalCount === 1 ? "entry" : "entries"} in ledger
        </div>
      </div>

      {/* TOTAL EXPENSES */}
      <div
        className="rounded-2xl p-5 border border-stone-200 bg-white shadow-sm"
        data-testid="summary-total-expenses-card"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">
          Total Expenses
        </div>
        <div
          className="mt-2 font-heading font-semibold tabular-nums text-2xl text-red-700"
          data-testid="summary-total-expenses"
        >
          {loading ? "—" : `₹${Math.round(Number(totalExpenses) || 0).toLocaleString("en-IN")}`}
        </div>
        <div className="mt-1 text-[11px] text-stone-500">All debit entries</div>
      </div>

      {/* TOTAL RECEIVED */}
      <div
        className="rounded-2xl p-5 border border-stone-200 bg-white shadow-sm"
        data-testid="summary-total-received-card"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">
          Total Received
        </div>
        <div
          className="mt-2 font-heading font-semibold tabular-nums text-2xl text-emerald-700"
          data-testid="summary-total-received"
        >
          {loading ? "—" : `₹${Math.round(Number(totalReceived) || 0).toLocaleString("en-IN")}`}
        </div>
        <div className="mt-1 text-[11px] text-stone-500">All credit entries</div>
      </div>

      {/* NET (Received - Expenses) */}
      <div
        className="rounded-2xl p-5 border border-stone-200 bg-white shadow-sm"
        data-testid="summary-net-card"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">
          Net
        </div>
        <div
          className={`mt-2 font-heading font-semibold tabular-nums text-2xl ${
            netNegative ? "text-red-700" : "text-stone-900"
          }`}
          data-testid="summary-net"
        >
          {loading ? "—" : inrBalance(net)}
        </div>
        <div className="mt-1 text-[11px] text-stone-500">Received − Expenses</div>
      </div>
    </section>
  );
}


// ---------- Row actions kebab menu -------------------------------------------
function RowActions({ onEdit, onDuplicate, onDelete, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} className="relative flex justify-end">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1 rounded hover:bg-stone-100 text-stone-500"
        aria-label="Row actions"
        data-testid={`office-expenses-actions-${testid}`}
      >
        <MoreVertical className="w-4 h-4" strokeWidth={2} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[140px] text-xs"
          role="menu"
          data-testid={`office-expenses-actions-menu-${testid}`}
        >
          <button
            type="button"
            onClick={() => { setOpen(false); onEdit(); }}
            className="w-full text-left px-3 py-1.5 hover:bg-stone-50 inline-flex items-center gap-2 text-stone-700"
            data-testid={`office-expenses-action-edit-${testid}`}
          >
            <Edit2 className="w-3.5 h-3.5" strokeWidth={2} /> Edit
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onDuplicate(); }}
            className="w-full text-left px-3 py-1.5 hover:bg-stone-50 inline-flex items-center gap-2 text-stone-700"
            data-testid={`office-expenses-action-duplicate-${testid}`}
          >
            <Copy className="w-3.5 h-3.5" strokeWidth={2} /> Duplicate
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(); }}
              className="w-full text-left px-3 py-1.5 hover:bg-red-50 inline-flex items-center gap-2 text-red-700"
              data-testid={`office-expenses-action-delete-${testid}`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={2} /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Inline edit row --------------------------------------------------
function EditRow({ row, onSave, onCancel }) {
  const [form, setForm] = useState({
    date: row.date,
    reason: row.reason,
    category: row.category || "",
    transaction_type: row.transaction_type,
    amount: String(row.amount),
  });
  const canSave = form.date && form.reason.trim() && Number(form.amount) > 0;
  return (
    <tr className="bg-amber-50/40" data-testid={`office-expenses-edit-row-${row.id}`}>
      <td className="px-3 py-2">
        <input type="date" className="input py-1 text-xs w-full" value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
      </td>
      <td className="px-3 py-2">
        <input type="text" className="input py-1 text-xs w-full" value={form.reason}
          onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
      </td>
      <td className="px-3 py-2">
        <input type="text" list="office-cat-suggestions" className="input py-1 text-xs w-full" value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} />
      </td>
      <td className="px-3 py-2" colSpan={2}>
        <div className="flex items-center gap-2">
          <select
            className="input py-1 text-xs"
            value={form.transaction_type}
            onChange={(e) => setForm((f) => ({ ...f, transaction_type: e.target.value }))}
          >
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>
          <input type="number" min={0} className="input py-1 text-xs text-right w-24" value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
        </div>
      </td>
      <td className="px-3 py-2" />
      <td className="px-2 py-2">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            disabled={!canSave}
            onClick={() => onSave({
              date: form.date, reason: form.reason.trim(),
              category: form.category.trim(),
              transaction_type: form.transaction_type,
              amount: Number(form.amount),
            })}
            className="text-[11px] px-2 py-1 rounded bg-rose-700 text-white font-semibold disabled:opacity-50"
            data-testid={`office-expenses-edit-save-${row.id}`}
          >
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[11px] px-2 py-1 rounded bg-stone-100 text-stone-700 font-semibold"
            data-testid={`office-expenses-edit-cancel-${row.id}`}
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------- Quick entry form -------------------------------------------------
function EntryForm({ onSubmit, loading }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, reason: "", category: "",
    transaction_type: "debit", amount: "",
  });
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef(null);

  const canSubmit =
    !!form.date &&
    form.reason.trim().length > 0 &&
    Number.isFinite(Number(form.amount)) &&
    Number(form.amount) > 0 &&
    !busy && !loading;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onSubmit({
        date: form.date,
        reason: form.reason.trim(),
        category: form.category.trim(),
        transaction_type: form.transaction_type,
        amount: Number(form.amount),
      });
      setForm({ date: form.date, reason: "", category: form.category, transaction_type: form.transaction_type, amount: "" });
      reasonRef.current?.focus?.();
    } finally { setBusy(false); }
  };

  return (
    <>
      <datalist id="office-cat-suggestions">
        {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
      </datalist>
      <form
        onSubmit={submit}
        className="card p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 items-end"
        data-testid="office-expenses-form"
      >
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 block mb-1">Date</label>
          <input
            type="date"
            className="input w-full"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            required
            data-testid="office-expenses-form-date"
          />
        </div>
        <div className="sm:col-span-3">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 block mb-1">Reason</label>
          <input
            ref={reasonRef}
            type="text"
            className="input w-full"
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            placeholder="e.g. PETROL [ADITYA]"
            maxLength={200}
            required
            data-testid="office-expenses-form-reason"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 block mb-1">Category</label>
          <input
            type="text"
            list="office-cat-suggestions"
            className="input w-full"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value.toUpperCase() }))}
            placeholder="Optional"
            maxLength={60}
            data-testid="office-expenses-form-category"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 block mb-1">Type</label>
          <div className="flex rounded-lg border border-stone-200 overflow-hidden text-xs font-semibold" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={form.transaction_type === "debit"}
              onClick={() => setForm((f) => ({ ...f, transaction_type: "debit" }))}
              className={`flex-1 px-2 py-2 inline-flex items-center justify-center gap-1 transition ${
                form.transaction_type === "debit" ? "bg-red-100 text-red-800" : "bg-white text-stone-500 hover:bg-stone-50"
              }`}
              data-testid="office-expenses-form-type-debit"
            >
              <TrendingDown className="w-3.5 h-3.5" strokeWidth={2} /> Debit
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={form.transaction_type === "credit"}
              onClick={() => setForm((f) => ({ ...f, transaction_type: "credit" }))}
              className={`flex-1 px-2 py-2 inline-flex items-center justify-center gap-1 border-l border-stone-200 transition ${
                form.transaction_type === "credit" ? "bg-emerald-100 text-emerald-800" : "bg-white text-stone-500 hover:bg-stone-50"
              }`}
              data-testid="office-expenses-form-type-credit"
            >
              <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} /> Credit
            </button>
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 block mb-1">Amount</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">₹</span>
            <input
              type="number"
              min="0"
              step="any"
              className="input w-full pl-7"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="0"
              required
              data-testid="office-expenses-form-amount"
            />
          </div>
        </div>
        <div className="sm:col-span-1">
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn btn-primary btn-sm w-full inline-flex items-center justify-center gap-1"
            data-testid="office-expenses-form-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.2} />}
            Add
          </button>
        </div>
      </form>
    </>
  );
}

// ---------- Archive drawer ---------------------------------------------------
function ArchiveDrawer({ onClose, onChanged, canWrite, canDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "", date_from: "", date_to: "", txn_type: "", category: "",
  });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/team/office-expenses", { params });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load archive");
    } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const enriched = useMemo(() => {
    let bal = 0;
    return rows.map((r) => {
      const debit = r.transaction_type === "debit" ? Number(r.amount) || 0 : 0;
      const credit = r.transaction_type === "credit" ? Number(r.amount) || 0 : 0;
      bal = Math.round((bal + credit - debit) * 100) / 100;
      return { ...r, debit, credit, running_balance: bal };
    });
  }, [rows]);

  // Month-wise grouping (newest first per group; groups newest first).
  const grouped = useMemo(() => {
    const map = new Map();
    enriched.forEach((r) => {
      const key = (r.date || "").slice(0, 7) || "0000-00";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([month, items]) => ({ month, items: items.slice().reverse() }));
  }, [enriched]);

  const totals = useMemo(() => {
    const d = enriched.reduce((s, r) => s + r.debit, 0);
    const c = enriched.reduce((s, r) => s + r.credit, 0);
    return { debit: d, credit: c, net: c - d };
  }, [enriched]);

  const buildQuery = () => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.append(k, v); });
    return q.toString();
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/team/office-expenses/export.csv?${buildQuery()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      _download(blob, `cashbook-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const exportPdf = async () => {
    try {
      const res = await fetch(`${API}/team/office-expenses/export.pdf?${buildQuery()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      _download(blob, `cashbook-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const removeRow = async (id) => {
    const row = rows.find((r) => r.id === id);
    const label = row ? `${row.reason} (${row.date})` : "this entry";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/team/office-expenses/${id}`);
      await load();
      onChanged?.();
      toast.success("Entry deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const duplicateRow = async (id) => {
    try {
      await api.post(`/team/office-expenses/${id}/duplicate`);
      await load();
      onChanged?.();
      toast.success("Entry duplicated");
    } catch (e) {
      toast.error("Could not duplicate");
    }
  };

  const saveEdit = async (id, patch) => {
    try {
      await api.patch(`/team/office-expenses/${id}`, patch);
      setEditingId(null);
      await load();
      onChanged?.();
      toast.success("Entry updated");
    } catch (e) {
      toast.error("Could not update");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="office-expenses-archive-drawer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-5xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Cash Book</div>
            <h2 className="font-heading text-xl font-semibold text-stone-900 flex items-center gap-2">
              <Archive className="w-5 h-5 text-rose-700" strokeWidth={1.8} /> Archive
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportCsv}
              className="btn btn-outline btn-sm inline-flex items-center gap-1.5"
              data-testid="office-expenses-export-csv"
            >
              <Download className="w-4 h-4" strokeWidth={2} /> CSV
            </button>
            <button
              type="button"
              onClick={exportPdf}
              className="btn btn-outline btn-sm inline-flex items-center gap-1.5"
              data-testid="office-expenses-export-pdf"
            >
              <FileText className="w-4 h-4" strokeWidth={2} /> PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded hover:bg-stone-100"
              aria-label="Close archive"
              data-testid="office-expenses-archive-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-stone-100 bg-stone-50/50">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
              <input
                type="text"
                className="input pl-8 w-full text-sm"
                placeholder="Search reason or category…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                data-testid="office-expenses-archive-search"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 block mb-0.5">From</label>
              <input type="date" className="input text-sm w-full" value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                data-testid="office-expenses-archive-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 block mb-0.5">To</label>
              <input type="date" className="input text-sm w-full" value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                data-testid="office-expenses-archive-to" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                className="input text-sm"
                value={filters.txn_type}
                onChange={(e) => setFilters((f) => ({ ...f, txn_type: e.target.value }))}
                data-testid="office-expenses-archive-type"
              >
                <option value="">All types</option>
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>
              <input
                type="text"
                list="office-cat-suggestions"
                className="input text-sm"
                placeholder="Category"
                value={filters.category}
                onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value.toUpperCase() }))}
                data-testid="office-expenses-archive-category"
              />
            </div>
          </div>
          {(filters.q || filters.date_from || filters.date_to || filters.txn_type || filters.category) && (
            <button
              type="button"
              onClick={() => setFilters({ q: "", date_from: "", date_to: "", txn_type: "", category: "" })}
              className="mt-2 text-[11px] text-stone-500 hover:text-rose-700 underline underline-offset-2 inline-flex items-center gap-1"
              data-testid="office-expenses-archive-clear"
            >
              <Filter className="w-3 h-3" /> Clear filters
            </button>
          )}
        </div>

        <div className="px-6 py-3 border-b border-stone-100 flex items-center gap-4 text-xs text-stone-600" data-testid="office-expenses-archive-totals">
          <span>{rows.length} entries</span>
          <span className="text-red-700 font-mono">Debit: -₹{Math.round(totals.debit).toLocaleString("en-IN")}</span>
          <span className="text-emerald-700 font-mono">Credit: +₹{Math.round(totals.credit).toLocaleString("en-IN")}</span>
          <span className={`font-mono font-semibold ${totals.net < 0 ? "text-red-800" : "text-stone-900"}`}>
            Net: {inrBalance(totals.net)}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && (
            <div className="text-center text-stone-400 text-sm py-10">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          )}
          {!loading && grouped.length === 0 && (
            <div className="text-center text-stone-400 text-sm py-10" data-testid="office-expenses-archive-empty">
              No entries match your filters.
            </div>
          )}
          {grouped.map((group) => (
            <div key={group.month} data-testid={`office-expenses-archive-month-${group.month}`}>
              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-2">
                {monthLabel(group.month)}
              </div>
              <div className="rounded-xl border border-stone-200 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold text-left">
                      <th className="px-3 py-2 w-28">Date</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2 w-28">Category</th>
                      <th className="px-3 py-2 text-right w-28">Debit</th>
                      <th className="px-3 py-2 text-right w-28">Credit</th>
                      <th className="px-3 py-2 text-right w-32">Balance</th>
                      {canWrite && <th className="px-3 py-2 w-16 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {group.items.map((r) => (
                      editingId === r.id ? (
                        <EditRow key={r.id} row={r} onSave={(p) => saveEdit(r.id, p)}
                          onCancel={() => setEditingId(null)} canWrite={canWrite} />
                      ) : (
                        <tr key={r.id} data-testid={`office-expenses-archive-row-${r.id}`}>
                          <td className="px-3 py-2 font-mono text-xs text-stone-600 whitespace-nowrap">{formatDMY(r.date)}</td>
                          <td className="px-3 py-2 text-stone-800">{r.reason}</td>
                          <td className="px-3 py-2 text-xs">
                            {r.category ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 text-[10px] uppercase tracking-wider font-semibold">
                                {r.category}
                              </span>
                            ) : <span className="text-stone-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-mono text-red-700">
                            {r.debit > 0 ? `-₹${Math.round(r.debit).toLocaleString("en-IN")}` : ""}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-mono text-emerald-700">
                            {r.credit > 0 ? `+₹${Math.round(r.credit).toLocaleString("en-IN")}` : ""}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums font-mono font-semibold ${r.running_balance < 0 ? "text-red-800" : "text-stone-800"}`}>
                            {inrBalance(r.running_balance)}
                          </td>
                          {canWrite && (
                            <td className="px-2 py-2">
                              <RowActions
                                onEdit={() => setEditingId(r.id)}
                                onDuplicate={() => duplicateRow(r.id)}
                                onDelete={canDelete ? () => removeRow(r.id) : null}
                                testid={`archive-${r.id}`}
                              />
                            </td>
                          )}
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Helpers ----------------------------------------------------------
function formatDMY(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y.slice(-2)}`;
}

function inrBalance(n) {
  const num = Math.round(Number(n) || 0);
  const sign = num < 0 ? "-" : "";
  return `${sign}₹${Math.abs(num).toLocaleString("en-IN")}`;
}

function monthLabel(ym) {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function _download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
